import React, { useContext, useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Box, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  ICompany,
  ICompanyType,
  IMarketActivity,
  useCompanyTypesLazyQuery,
  useMarketActivitiesLazyQuery,
  useUpdateCompanyMutation
} from '../../../../generated/graphql';
import { sameCompanyNameExists, similarCompanyNameExists } from '../../../../utils/clientUtils';
import { canUserEdit } from '../../../../utils/rightUtils';
import { Colors } from '../../../../utils/theme';
import EditButton from '../../../Common/Buttons/EditButton';
import { validWebsiteRegex } from '../../../../utils/ui';
import CancelButton from '../../../Common/Buttons/CancelButton';
import SaveButton from '../../../Common/Buttons/SaveButton';
import InputComponent from '../../../Common/Fields/InputComponent';
import AutocompleteComponent from '../../../Common/Fields/AutocompleteComponent';

interface IProps {
  company: ICompany;
}

type IErrors = {
  companyName: string | null;
  website: string | null;
  companyType: string | null;
  marketActivity: string | null;
  updateError?: ApolloError;
};

const defaultErrorsObj: IErrors = {
  companyName: null,
  website: null,
  companyType: null,
  marketActivity: null
};

const CompanyGeneralTab: React.FunctionComponent<IProps> = ({ company }: IProps) => {
  const [editable, setEditable] = useState(false);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [companyName, setCompanyName] = useState(company.name);
  const [website, setWebsite] = useState(company.website);
  const [stockCode, setStockCode] = useState(company.unique_stock_code);
  const [companyType, setCompanyType] = useState<ICompanyType | null>(company.company_type || null);
  const [marketActivity, setMarketActivity] = useState<IMarketActivity | null>(
    company.market_activity || null
  );
  const [info, setInfo] = useState(company.info);
  const [memberIndex, setMemberIndex] = useState(company.member_index);
  const [marketSecondSpecialty, setMarketSecondSpecialty] = useState(
    company.market_second_specialty
  );
  const [companyTypesOptions, setCompanyTypesOptions] = useState([]);
  const [marketActivitiesOptions, setMarketActivitiesOptions] = useState([]);
  const [getCompanyTypes, companyTypesData] = useCompanyTypesLazyQuery();
  const [getMarketActivities, marketActivitiesData] = useMarketActivitiesLazyQuery();
  const [updateCompany, updateCompanyResult] = useUpdateCompanyMutation();
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Company);

  useEffect(() => {
    if (companyTypesData.data && companyTypesData.data.result) {
      setCompanyTypesOptions(companyTypesData.data.result as never[]);
    }
    if (marketActivitiesData.data && marketActivitiesData.data.result) {
      setMarketActivitiesOptions(marketActivitiesData.data.result as never[]);
    }
  }, [companyTypesData, marketActivitiesData]);

  const handleCompanyNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.currentTarget;

    if (value.length < 2) {
      setFormErrors({
        ...formErrors,
        [name]: 'Company name too short'
      });
    } else {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
    setCompanyName(value);
  };

  const handleWebsiteChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.currentTarget;
    const isValidWebsite = value?.match(validWebsiteRegex);

    if (isValidWebsite || value.length === 0) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    } else {
      setFormErrors({
        ...formErrors,
        [name]: 'Invalid website'
      });
    }

    setWebsite(value);
  };

  const handleStockCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    setStockCode(value);
  };

  const handleInfoChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    setInfo(value);
  };

  const handleMemberIndexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setMemberIndex(value === 'true' ? true : false);
  };

  const handleMarketSecondSpecialtyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setMarketSecondSpecialty(value);
  };

  const handleEditClicked = () => {
    if (companyTypesOptions.length === 0 || marketActivitiesOptions.length === 0) {
      getCompanyTypes();
      getMarketActivities();
    }
    setEditable(true);
  };

  const handleCompanyTypeChanged = (_: unknown, newValue: ICompanyType | null) => {
    if (newValue) {
      setFormErrors({ ...formErrors, companyType: null });
    } else {
      setFormErrors({ ...formErrors, companyType: 'Company type is required' });
    }
    setCompanyType(newValue);
  };

  const handleMarketActivityChanged = (_: unknown, newValue: IMarketActivity | null) => {
    if (newValue) {
      setFormErrors({ ...formErrors, marketActivity: null });
    } else {
      setFormErrors({ ...formErrors, marketActivity: 'Market activity is required' });
    }

    setMarketActivity(newValue);
  };

  const handleCancel = () => {
    setCompanyName(company.name);
    setWebsite(company.website);
    setInfo(company.info);
    setCompanyType(company.company_type || null);
    setMarketActivity(company.market_activity || null);
    setMemberIndex(company.member_index);
    setMarketSecondSpecialty(company.market_second_specialty);
    setEditable(false);
    setFormErrors(defaultErrorsObj);
  };

  const handleSave = () => {
    let hasErrors = false;
    const errors: IErrors = {
      companyName: null,
      website: null,
      companyType: null,
      marketActivity: null
    };
    if (!companyName || companyName.length < 2) {
      hasErrors = true;
      errors.companyName = 'Company name too short';
    }
    const isValidWebsite = website?.match(validWebsiteRegex);
    if (website && website?.length > 0 && !isValidWebsite) {
      hasErrors = true;
      errors.website = 'Invalid website';
    }
    if (companyType === null) {
      hasErrors = true;
      errors.companyType = 'Company type is required';
    }
    if (marketActivity === null) {
      hasErrors = true;
      errors.marketActivity = 'Market activity is required';
    }
    if (hasErrors) {
      setFormErrors({ ...errors });
    } else {
      const nameChanged = company.name !== companyName;
      const nameExists = nameChanged
        ? sameCompanyNameExists(companyName as string, parseInt(company.id as string))
        : false;
      if (nameExists) {
        setFormErrors({ ...formErrors, companyName: 'Company name exists' });
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: 'Company name exists',
            severity: 'error'
          }
        });
      } else {
        const hasSimilarCompanyName = nameChanged
          ? similarCompanyNameExists(companyName as string, parseInt(company.id as string))
          : false;
        if (hasSimilarCompanyName) {
          dispatch({
            type: Actions.ShowConfirmation,
            payload: {
              text: 'There is company with similar name. Are you sure you want to continue?',
              agreeAction: handleUpdateCompany
            }
          });
        } else {
          handleUpdateCompany();
        }
      }
    }
  };

  const handleUpdateCompany = () => {
    dispatch({ type: Actions.HideConfirmation });
    updateCompany({
      variables: {
        id: parseInt(company.id as string),
        input: {
          name: companyName as string,
          company_type_id: parseInt(companyType?.id as string),
          market_activity_id: parseInt(marketActivity?.id as string),
          website,
          member_index: memberIndex,
          market_second_specialty: marketSecondSpecialty,
          unique_stock_code: stockCode
        }
      },
      update(_, { data }) {
        if (data && data.result) {
          setEditable(false);
          dispatch({
            type: Actions.SetSelectedCompany,
            payload: {
              name: data.result.name,
              address_name: data.result.main_address?.name
            }
          });
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Successfully updated ${companyName}`,
              severity: 'success',
              autoHide: 2000
            }
          });
        }
      }
    }).catch((e) => setFormErrors({ ...formErrors, updateError: e.message.slice(15) }));
  };

  return (
    <Box sx={{ padding: '80px', position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
        <EditButton
          id="contact-general-edit-button"
          onClick={handleEditClicked}
          disabled={!canEdit || editable}
          editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      {formErrors.updateError && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography color="error">{formErrors.updateError.message}</Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: '7.2%',
          paddingX: '5%'
        }}
      >
        <Box sx={{ display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <InputComponent
            id="name"
            label="Company Name(full)"
            value={companyName}
            onChange={handleCompanyNameChange}
            disabled={!editable}
            errorMsg={formErrors.companyName}
          />
          <AutocompleteComponent
            id="company-type"
            label="Company type"
            options={companyTypesOptions}
            getOptionLabel={(option: ICompanyType) => option.name}
            disabled={!editable}
            values={companyType}
            onChange={handleCompanyTypeChanged}
            errorMsg={formErrors.companyType}
          />
          <AutocompleteComponent
            id="market-activity"
            label="Market activity"
            options={marketActivitiesOptions}
            getOptionLabel={(option: IMarketActivity) => option.name}
            disabled={!editable}
            values={marketActivity}
            onChange={handleMarketActivityChanged}
            errorMsg={formErrors.marketActivity}
          />
          <InputComponent
            id="market-second-specialty"
            label="Market Second Specialty"
            value={marketSecondSpecialty ?? '-'}
            onChange={handleMarketSecondSpecialtyChange}
            disabled={!editable}
          />
          <Box>
            <RadioGroup row value={memberIndex} onChange={handleMemberIndexChange}>
              <FormLabel
                sx={{
                  color: (t) => (editable ? t.palette.primary.main : Colors.gray),
                  marginTop: '13px',
                  marginRight: '40px'
                }}
                component="legend"
              >
                Epra Watch Index
              </FormLabel>
              <FormControlLabel
                value={true}
                control={<Radio color="primary" disabled={!editable} />}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio color="primary" disabled={!editable} />}
                label="No"
              />
            </RadioGroup>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <InputComponent
            id="website"
            label="Website"
            value={website ?? '-'}
            onChange={handleWebsiteChange}
            disabled={!editable}
            errorMsg={formErrors.website}
          />
          <InputComponent
            id="stock-code"
            label="Unique Stock Code"
            value={stockCode ?? '-'}
            onChange={handleStockCodeChange}
            disabled={!editable}
          />
          <InputComponent
            id="company-info"
            label="Company Info"
            value={info ?? ''}
            onChange={handleInfoChange}
            disabled={!editable}
            multiline
            rows={4}
            additionalStyle={{
              '& .MuiInputBase-root': {
                height: 106
              }
            }}
          />
        </Box>
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '140px' }}>
          <CancelButton
            id="cancel-button"
            disabled={updateCompanyResult.loading}
            onClick={handleCancel}
            text="Cancel"
          />
          <SaveButton
            id="save-button"
            disabled={updateCompanyResult.loading}
            onClick={handleSave}
            text="Save"
          />
        </Box>
      )}
    </Box>
  );
};

export default CompanyGeneralTab;
